import * as React from 'react';
import { CircledExclamationMarkIcon, FullStreamingIcon, LoaderIcon, LucidIcon } from './Icons';
import { FilespaceCardTopRow } from './filespaceCardComponents/FilespaceCardTopRow';
import FilespaceCardTitleSection from './filespaceCardComponents/FilespaceCardTitleSection';
import FilespaceCardInfo from './filespaceCardComponents/FilespaceCardInfoSection';
import FilespaceCardButton from './filespaceCardComponents/FilespaceCardButton';
import { Event, Filespace, FilespaceExtended, FilespaceStorage } from 'fs-api-client';
import { LogManager } from '../../utils/LogManager';
import { TransitionType } from '../../utils/types';
import TypeEnum = Filespace.TypeEnum;
import StatusEnum = Filespace.StatusEnum;
import RenameStatusEnum = FilespaceExtended.RenameStatusEnum;

const DISABLE_FILESPACE_CREATION = process.env.DISABLE_FILESPACE_CREATION || false;
const logManager = new LogManager('FilespaceCard');

const canDeleteFilespace = (filespace: Filespace): boolean => {
    const nonDeletableStatuses: Array<
        Filespace.SuspensionStatusEnum |
        Filespace.StatusEnum |
        Filespace.RenameStatusEnum
        > = [
        Filespace.SuspensionStatusEnum.Suspending,
        Filespace.SuspensionStatusEnum.SuspensionFailed,
        Filespace.SuspensionStatusEnum.Unsuspending,
        Filespace.SuspensionStatusEnum.UnsuspensionFailed,
        Filespace.SuspensionStatusEnum.Errored,
        Filespace.StatusEnum.Errored,
        Filespace.RenameStatusEnum.Errored,
        Filespace.RenameStatusEnum.Inprogress
    ];
    const result = !nonDeletableStatuses.includes(filespace.suspensionStatus!) &&
        !nonDeletableStatuses.includes(filespace.status!) &&
        !nonDeletableStatuses.includes(filespace.renameStatus!);
    return result;
};

const canRenameFilespace = (filespace: Filespace): boolean => {
    const properRenameStatus = filespace.renameStatus === undefined ||
        filespace.renameStatus === RenameStatusEnum.Success;
    const properFsStatus = filespace.status === Filespace.StatusEnum.Initialized &&
        filespace.suspensionStatus === Filespace.SuspensionStatusEnum.NotSuspended &&
        properRenameStatus;
    const isV4Beta = filespace.type === TypeEnum.V4beta;
    const canRename = properFsStatus && !isV4Beta;
    return canRename;
};

export enum CardType {
    Sandbox,
    SandboxUninitialized,
    LucidLinkAWS,
    LucidLinkAWSAdvanced,
    LucidLinkAWSUninitialized,
    LucidLinkAWSAdvancedUninitialized,
    LucidLinkIBMTeams,
    LucidLinkIBMTeamsUninitialized,
    LucidLinkIBMEnterprise,
    LucidLinkIBMEnterpriseUninitialized,
    LucidLinkIBMAdvanced,
    LucidLinkIBMAdvancedUninitialized,
    LucidLinkWasabi,
    LucidLinkWasabiUninitialized,
    Customer,
    CustomerUninitialized,
    Provisioning,
    CreateFirst,
    CreateNew,
    Renaming
}

const LUCIDLINK_PERFORMANCE_TEXT = {
    [CardType.LucidLinkAWS]: 'Classic Performance',
    [CardType.LucidLinkIBMEnterprise]: 'Classic Enterprise',
    [CardType.LucidLinkIBMAdvanced]: 'Classic Advanced',
    [CardType.LucidLinkAWSAdvanced]: 'Classic Enterprise',
    [CardType.LucidLinkAWSUninitialized]: 'Classic Performance',
    [CardType.LucidLinkIBMEnterpriseUninitialized]: 'Classic Enterprise',
    [CardType.LucidLinkIBMAdvancedUninitialized]: 'Classic Advanced',
    [CardType.LucidLinkAWSAdvancedUninitialized]: 'Classic Enterprise',
};

// @TODO remove filespace props and use filespace directly
interface FilespaceCardProperties {
    transitionType: TransitionType;
    cardType: CardType;
    isFilespaceSuspended?: boolean;
    isUserSuspended?: boolean;
    region?: string;
    nodes?: number;
    provider?: FilespaceStorage.ProviderEnum;
    connectedDevices?: number;
    onClick?: () => void;
    domainName: string;
    userEmail: string;
    filespace: Filespace | null;
}

function onClickCreate(userEmail: string, elementId: string, isDisabled?: boolean) {
    const disabledTextId = elementId + 'disabledText';
    if (document.getElementById(disabledTextId)) {
        logManager.log(`User ${userEmail} clicked create filespace card`, true, Event.EventTypeEnum.Filespace);
        return;
    }
    const disabledText = document.createElement('div');
    disabledText.id = disabledTextId;
    disabledText.innerHTML = 'Due to limited availability we have temporarily disabled filespace creation. We\'ll personally inform you once the service is back.';
    const el = document.getElementById(elementId);
    if (el) {
        el.appendChild(disabledText);
    }
    logManager.log(`User ${userEmail} clicked create filespace card`, true, Event.EventTypeEnum.Filespace);
}

class FilespaceCard extends React.Component<FilespaceCardProperties, any> {

    constructor(props: any) {
        super(props);

        this.getCardClassList = this.getCardClassList.bind(this);
        this.determineClassNameBasedOnTransitionTypeAndFilespaceVersion =
        this.determineClassNameBasedOnTransitionTypeAndFilespaceVersion.bind(this);
        FilespaceCard.determineFSCardIcon = FilespaceCard.determineFSCardIcon.bind(this);
        this.getInfoMessage = this.getInfoMessage.bind(this);
        this.isInitialized = this.isInitialized.bind(this);
    }

    render() {
        let card: JSX.Element | null = null;
        const createFsDisabled = DISABLE_FILESPACE_CREATION;
        const createFsClassNames = createFsDisabled ? 'flex-card add' : 'ttip flex-card add';
        const isDeleteDisabled = this.props.filespace ? !canDeleteFilespace(this.props.filespace) : false;
        const isRenameDisabled = this.props.filespace ? !canRenameFilespace(this.props.filespace) : false;

        switch (this.props.cardType) {
            case CardType.CreateFirst:
                if (this.props.isUserSuspended) {
                    return null;
                }
                card = (
                    <span
                        id="create-fs-id"
                        onClick={
                            createFsDisabled ?
                                () => {
                                    onClickCreate(this.props.userEmail, 'create-fs-id', this.props.isFilespaceSuspended);
                                } :
                                this.props.onClick
                        }
                        className={createFsClassNames}
                    >
                        <h2>
                            {LucidIcon({width: '48px', height: '40px'})}
                        </h2>
                        <span className="bottom link">Create your first Filespace</span>
                    </span>
                );
                break;
            case CardType.CreateNew:
                if (this.props.isUserSuspended) {
                    return null;
                }
                card = (
                    <span
                        id="create-first-fs-id"
                        onClick={
                            this.props.isFilespaceSuspended ?
                                () => {
                                    onClickCreate(this.props.userEmail, 'create-first-fs-id', this.props.isFilespaceSuspended);
                                } :
                                this.props.onClick
                        }
                        className={createFsClassNames}
                    >
                        <h2>
                            {LucidIcon({width: '48px', height: '40px'})}
                        </h2>
                        <span className="bottom link">Create a new Filespace.</span>
                    </span>
                );
                break;
            case CardType.Provisioning:
                if (!this.props.filespace?.name) {
                    return;
                }

                card = (
                    <section onClick={this.props.onClick} className={this.getCardClassList('flex-card processing')}>
                        <h2>
                            {LoaderIcon({width: '60px', height: '60px'})}
                        </h2>
                        <div className="bottom">
                            <strong className="wrapWord">{this.props.filespace?.name}</strong> Setting up your Filespace... <br/>(could take up to a
                            minute)
                        </div>
                    </section>
                );
                break;
            case CardType.Renaming:
                if (!this.props.filespace?.name) {
                    return;
                }

                card = (
                    <section onClick={this.props.onClick} className={this.getCardClassList('flex-card processing')}>
                        <h2>
                            {LoaderIcon({width: '60px', height: '60px'})}
                        </h2>
                        <div className="bottom">
                            <strong className="wrapWord">{this.props.filespace?.name}</strong> Renaming your Filespace... <br/>
                            (it could take up to 30 minutes)
                        </div>
                    </section>
                );
                break;
            case CardType.Sandbox:
            case CardType.LucidLinkIBMTeams:
                if (
                    !this.props.filespace?.name ||
                    !this.props.provider
                ) {
                    return null;
                }

                card = (
                    <section onClick={this.props.onClick} className={this.getCardClassList('flex-card green')}>
                        <FilespaceCardTopRow
                            filespaceId={this.props.filespace?.id!}
                            filespaceName={this.props.filespace?.name}
                            text={this.props.cardType === CardType.Sandbox ? 'LucidLink storage 20 GB' : 'LucidLink Teams'}
                            isDeleteDisabled={isDeleteDisabled}
                            isRenameDisabled={isRenameDisabled}
                            icon={FilespaceCard.determineFSCardIcon(this.props.filespace?.version!, this.props.filespace?.type)}
                        />
                        <FilespaceCardTitleSection
                            title={this.props.filespace?.name}
                            description={this.getInfoMessage()}
                        />
                        <FilespaceCardInfo
                            provider={this.props.provider}
                            region={this.props.region}
                            filespaceSize={this.props.filespace?.stats.filespaceSize}
                            usedStorage={this.props.filespace?.stats.usedStorage}
                            connectedDevices={this.props.connectedDevices}
                        />
                        <FilespaceCardButton
                            isUserSuspended={this.props.isUserSuspended}
                            isFilespaceSuspended={this.props.isFilespaceSuspended}
                            isErrored={this.isErrored()}
                            filespaceName={this.props.filespace?.name}
                            domainName={this.props.domainName}
                            credentialsAreAdded={true}
                        />
                    </section>
                );
                break;
            case CardType.SandboxUninitialized:
            case CardType.LucidLinkIBMTeamsUninitialized:
                if (
                    !this.props.filespace?.name ||
                    !this.props.provider
                ) {
                    return null;
                }

                card = (
                    <section onClick={this.props.onClick} className={this.getCardClassList('flex-card green')}>
                        <FilespaceCardTopRow
                            filespaceId={this.props.filespace?.id!}
                            filespaceName={this.props.filespace?.name}
                            text={this.props.cardType === CardType.SandboxUninitialized ? 'LucidLink storage 20 GB' : 'LucidLink Teams'}
                            isDeleteDisabled={isDeleteDisabled}
                            isRenameDisabled={isRenameDisabled}
                            icon={FilespaceCard.determineFSCardIcon(this.props.filespace?.version!, this.props.filespace?.type)}
                        />
                        <FilespaceCardTitleSection
                            title={this.props.filespace?.name}
                            description={this.getInfoMessage()}
                        />
                        <FilespaceCardInfo
                            provider={this.props.provider}
                            region={this.props.region}
                        />
                        <FilespaceCardButton
                            isUserSuspended={this.props.isUserSuspended}
                            isFilespaceSuspended={this.props.isFilespaceSuspended}
                            filespaceName={this.props.filespace?.name}
                            isErrored={this.isErrored()}
                            domainName={this.props.domainName}
                            credentialsAreAdded={false}
                            isLegacySandboxStyle={true}
                            filespace={this.props.filespace}
                        />
                    </section>
                );
                break;
            case CardType.LucidLinkWasabiUninitialized:
                if (
                    !this.props.filespace?.name ||
                    !this.props.provider
                ) {
                    return null;
                }

                card = (
                    <section onClick={this.props.onClick} className={this.getCardClassList('flex-card grey')}>
                        <FilespaceCardTopRow
                            filespaceId={this.props.filespace?.id!}
                            filespaceName={this.props.filespace?.name}
                            text={this.props.filespace!.subscriptionType === Filespace.SubscriptionTypeEnum.None ? 'Classic Standard' : 'Classic Basic'}
                            isDeleteDisabled={isDeleteDisabled}
                            isRenameDisabled={isRenameDisabled}
                            icon={FilespaceCard.determineFSCardIcon(this.props.filespace?.version!, this.props.filespace?.type)}
                        />
                        <FilespaceCardTitleSection
                            title={this.props.filespace?.name}
                            description={this.getInfoMessage()}
                        />
                        <FilespaceCardInfo
                            provider={this.props.provider}
                            region={this.props.region}
                        />
                        <FilespaceCardButton
                            isUserSuspended={this.props.isUserSuspended}
                            isFilespaceSuspended={this.props.isFilespaceSuspended}
                            filespace={this.props.filespace}
                            isErrored={this.isErrored()}
                            filespaceName={this.props.filespace?.name}
                            domainName={this.props.domainName}
                            credentialsAreAdded={false}
                        />
                    </section>
                );
                break;
            case CardType.LucidLinkAWSUninitialized:
            case CardType.LucidLinkIBMEnterpriseUninitialized:
            case CardType.LucidLinkIBMAdvancedUninitialized:
            case CardType.LucidLinkAWSAdvancedUninitialized:
                if (
                    !this.props.filespace?.name ||
                    !this.props.provider
                ) {
                    return null;
                }

                card = (
                    <section onClick={this.props.onClick} className={this.getCardClassList('flex-card orange')}>
                        <FilespaceCardTopRow
                            filespaceId={this.props.filespace?.id!}
                            filespaceName={this.props.filespace?.name}
                            text={LUCIDLINK_PERFORMANCE_TEXT[this.props.cardType]}
                            isDeleteDisabled={isDeleteDisabled}
                            isRenameDisabled={isRenameDisabled}
                            icon={FilespaceCard.determineFSCardIcon(this.props.filespace?.version!, this.props.filespace?.type)}
                        />
                        <FilespaceCardTitleSection
                            title={this.props.filespace?.name}
                            description={this.getInfoMessage()}
                        />
                        <FilespaceCardInfo
                            provider={this.props.provider}
                            region={this.props.region}
                        />
                        <FilespaceCardButton
                            isUserSuspended={this.props.isUserSuspended}
                            isErrored={this.isErrored()}
                            isFilespaceSuspended={this.props.isFilespaceSuspended}
                            filespace={this.props.filespace}
                            filespaceName={this.props.filespace?.name}
                            domainName={this.props.domainName}
                            credentialsAreAdded={false}
                        />
                    </section>
                );
                break;
            case CardType.LucidLinkWasabi:
                if (
                    !this.props.filespace?.name ||
                    !this.props.provider
                ) {
                    return null;
                }

                card = (
                    <section onClick={this.props.onClick} className={this.getCardClassList('flex-card grey')}>
                        <FilespaceCardTopRow
                            filespaceId={this.props.filespace?.id!}
                            filespaceName={this.props.filespace?.name}
                            text={this.props.filespace!.subscriptionType === Filespace.SubscriptionTypeEnum.None ? 'Classic Standard' : 'Classic Basic'}
                            isDeleteDisabled={isDeleteDisabled}
                            isRenameDisabled={isRenameDisabled}
                            icon={FilespaceCard.determineFSCardIcon(this.props.filespace?.version!, this.props.filespace?.type)}
                        />
                        <FilespaceCardTitleSection
                            title={this.props.filespace?.name}
                            description={this.getInfoMessage()}
                        />
                        <FilespaceCardInfo
                            provider={this.props.provider}
                            region={this.props.region}
                            filespaceSize={this.props.filespace?.stats.filespaceSize}
                            usedStorage={this.props.filespace?.stats.usedStorage}
                            connectedDevices={this.props.connectedDevices}
                        />
                        <FilespaceCardButton
                            isUserSuspended={this.props.isUserSuspended}
                            isFilespaceSuspended={this.props.isFilespaceSuspended}
                            isErrored={this.isErrored()}
                            filespaceName={this.props.filespace?.name}
                            domainName={this.props.domainName}
                            credentialsAreAdded={true}
                        />
                    </section>
                );
                break;
            case CardType.LucidLinkAWS:
            case CardType.LucidLinkIBMEnterprise:
            case CardType.LucidLinkIBMAdvanced:
            case CardType.LucidLinkAWSAdvanced:
                if (
                    !this.props.filespace?.name ||
                    !this.props.provider
                ) {
                    return null;
                }

                card = (
                    <section onClick={this.props.onClick} className={this.getCardClassList('flex-card orange')}>
                        <FilespaceCardTopRow
                            filespaceId={this.props.filespace?.id!}
                            filespaceName={this.props.filespace?.name}
                            text={LUCIDLINK_PERFORMANCE_TEXT[this.props.cardType]}
                            isDeleteDisabled={isDeleteDisabled}
                            isRenameDisabled={isRenameDisabled}
                            icon={FilespaceCard.determineFSCardIcon(this.props.filespace?.version!, this.props.filespace?.type)}
                        />
                        <FilespaceCardTitleSection
                            title={this.props.filespace?.name}
                            description={this.getInfoMessage()}
                        />
                        <FilespaceCardInfo
                            provider={this.props.provider}
                            region={this.props.region}
                            filespaceSize={this.props.filespace?.stats.filespaceSize}
                            usedStorage={this.props.filespace?.stats.usedStorage}
                            connectedDevices={this.props.connectedDevices}
                        />
                        <FilespaceCardButton
                            filespaceName={this.props.filespace?.name}
                            domainName={this.props.domainName}
                            isErrored={this.isErrored()}
                            credentialsAreAdded={true}
                            isFilespaceSuspended={this.props.isFilespaceSuspended}
                            isUserSuspended={this.props.isUserSuspended}
                        />
                    </section>
                );
                break;
            case CardType.CustomerUninitialized:
                if (
                    !this.props.filespace?.name ||
                    !this.props.provider
                ) {
                    return null;
                }

                card = (
                    <section onClick={this.props.onClick} className={this.getCardClassList('flex-card blue')}>
                        <FilespaceCardTopRow
                            filespaceId={this.props.filespace?.id!}
                            filespaceName={this.props.filespace?.name}
                            text="LucidLink Custom"
                            isDeleteDisabled={isDeleteDisabled}
                            isRenameDisabled={isRenameDisabled}
                            icon={FilespaceCard.determineFSCardIcon(this.props.filespace?.version!, this.props.filespace?.type)}
                        />
                        <FilespaceCardTitleSection
                            title={this.props.filespace?.name}
                            description={this.getInfoMessage()}
                        />
                        <FilespaceCardInfo
                            provider={this.props.provider}
                            region={this.props.region}
                        />
                        <FilespaceCardButton
                            isFilespaceSuspended={this.props.isFilespaceSuspended}
                            filespace={this.props.filespace}
                            isErrored={this.isErrored()}
                            isUserSuspended={this.props.isUserSuspended}
                            filespaceName={this.props.filespace?.name}
                            domainName={this.props.domainName}
                            credentialsAreAdded={false}
                        />
                    </section>
                );
                break;
            case CardType.Customer:
                if (
                    !this.props.filespace?.name ||
                    !this.props.provider
                ) {
                    return null;
                }

                card = (
                    <section onClick={this.props.onClick} className={this.getCardClassList('flex-card blue')}>
                        <FilespaceCardTopRow
                            filespaceId={this.props.filespace?.id!}
                            filespaceName={this.props.filespace?.name}
                            text="LucidLink Custom"
                            isDeleteDisabled={isDeleteDisabled}
                            isRenameDisabled={isRenameDisabled}
                            icon={FilespaceCard.determineFSCardIcon(this.props.filespace?.version!, this.props.filespace?.type)}
                        />
                        <FilespaceCardTitleSection
                            title={this.props.filespace?.name}
                            description={this.getInfoMessage()}
                        />
                        <FilespaceCardInfo
                            provider={this.props.provider}
                            region={this.props.region}
                            filespaceSize={this.props.filespace?.stats.filespaceSize}
                            usedStorage={this.props.filespace?.stats.usedStorage}
                            connectedDevices={this.props.connectedDevices}
                        />
                        <FilespaceCardButton
                            isUserSuspended={this.props.isUserSuspended}
                            isFilespaceSuspended={this.props.isFilespaceSuspended}
                            isErrored={this.isErrored()}
                            filespaceName={this.props.filespace?.name}
                            domainName={this.props.domainName}
                            credentialsAreAdded={true}
                        />
                    </section>
                );
                break;
            default:
                card = null;
                break;
        }

        card = (
            <React.Fragment>
                {card}
            </React.Fragment>
        );

        return card;
    }

    private static shouldHaveStreamingIcon(filespaceVersion: number, filespaceType?: Filespace.TypeEnum) {
        const fsHasMetadataStreaming = filespaceVersion >= 4;
        const isNotV4Beta = filespaceType !== Filespace.TypeEnum.V4beta;
        const shouldHaveStreamingIcon = isNotV4Beta && fsHasMetadataStreaming;
        return shouldHaveStreamingIcon;
    }

    private getInfoMessage(): string {
        if (this.isRenameFailed()) {
            return 'Renaming your Filespace did not complete due to an error. ' +
                'Our Support team has been alerted and is actively working on resolving the problem.';
        }

        if (this.isErrored()) {
            return 'Your Filespace is currently not available. We are actively working on fixing the issue.';
        }

        if (!this.isInitialized()) {
            return 'One step away from using your Filespace';
        }

        return '';

    }

    private isRenameFailed(): boolean  {
        return !!this.props.filespace && this.props.filespace.renameStatus === Filespace.RenameStatusEnum.Errored;
    }

    private isInitialized(): boolean {
        return !!this.props.filespace && this.props.filespace.status === StatusEnum.Initialized;
    }

    private static determineFSCardIcon(filespaceVersion: number, filespaceType?: Filespace.TypeEnum): JSX.Element | null {
        const shouldHaveStreamingIcon = FilespaceCard.shouldHaveStreamingIcon(filespaceVersion, filespaceType);
        const iconSpan = <span><FullStreamingIcon width="17px" height="17px" /><CircledExclamationMarkIcon className="info" width="12" height="12" /></span>;
        return  shouldHaveStreamingIcon ? iconSpan : null;
    }

    private getCardClassList(cardClass: string): string {
        return [
            cardClass,
            this.determineClassNameBasedOnTransitionTypeAndFilespaceVersion(),
            this.isErrored() ? 'card-disable' : ''
        ].join(' ');
    }

    private isErrored(): boolean {
        if (!this.props.filespace) {
            return false;
        }

        const renameFailed = this.props.filespace.renameStatus === Filespace.RenameStatusEnum.Errored;
        const fsErrored = this.props.filespace.status === Filespace.StatusEnum.Errored;
        const failedSuspend = this.props.filespace.suspensionStatus === Filespace.SuspensionStatusEnum.SuspensionFailed;
        const failedUnsuspend = this.props.filespace.suspensionStatus === Filespace.SuspensionStatusEnum.UnsuspensionFailed;
        const erroredSuspend = this.props.filespace.suspensionStatus === Filespace.SuspensionStatusEnum.Errored;
        const result = renameFailed || fsErrored || failedSuspend || failedUnsuspend || erroredSuspend;
        return result;

    }

    private determineClassNameBasedOnTransitionTypeAndFilespaceVersion(): string {
        if (this.props.filespace?.version === 4 && this.props.filespace?.type === Filespace.TypeEnum.V4beta) {
            return 'beta';
        }
        if (this.props.filespace?.version === 3 && this.props.transitionType === TransitionType.V3ToV4) {
            return '';
        }
        if (this.props.filespace?.version === 3 && this.props.transitionType === TransitionType.None) {
            return '';
        }
        return 'v4';
    }
}

export default FilespaceCard;



// WEBPACK FOOTER //
// ./src/webportal/components/common/FilespaceCard.tsx